// src/lib/owners/ownerSelection.ts
//
// Pure logic for the project-record OWNER picker's IN-DROPDOWN selection.
// Extracted from the inline closure in src/App.tsx so the invariant can be
// unit-guarded under src/lib/** — see docs/REGRESSIONS.md.
//
// THE BUG this fixes (2026-07-01): on the Project Record "SELECT OWNERS"
// dropdown, clicking an owner row (e.g. "Johnny Test") did NOT check/highlight
// that owner's checkbox. Root cause: the row's `onClick` immediately called
// `setPrjOwnerPickerOpen(false)` (closing the dropdown) and opened a confirm
// dialog, and the checkbox's `isSelected` was derived ONLY from the persisted
// `owners_members` (which changes after commit) — so the checkbox could never
// show a checked state while the dropdown was open.
//
// THE FIX: clicking a row toggles a LOCAL pending selection (company-level,
// mirroring the all-owners-of-a-company semantics) that drives `isSelected`
// RIGHT AWAY and keeps the dropdown OPEN. Committing (the PATCH to
// /sub-admin/projects/:id/owner) happens on an explicit "Assign" action, not on
// every click — so real-project add/remove is unchanged, just deferred to the
// Assign button. These helpers are the pure core of that toggle + selected-state
// logic.

/**
 * Toggle a whole company's owner ids in the pending selection.
 *
 * Owners are COMPANY-LEVEL: clicking one owner toggles ALL active owners of that
 * same effective company together. If every id in `companyOwnerIds` is already
 * pending, they are all removed; otherwise the missing ones are added (dedup,
 * order preserved). Ids are compared as strings (company_id / user_id are UUID
 * strings — never coerce with Number()).
 */
export function toggleOwnerPendingIds(
  pending: Array<string | number> | null | undefined,
  companyOwnerIds: Array<string | number>,
): string[] {
  const ids = (Array.isArray(companyOwnerIds) ? companyOwnerIds : []).map(String)
  const cur = (Array.isArray(pending) ? pending : []).map(String)
  if (ids.length === 0) return cur
  const allPresent = ids.every((id) => cur.includes(id))
  if (allPresent) return cur.filter((id) => !ids.includes(id))
  const toAdd = ids.filter((id) => !cur.includes(id))
  return [...cur, ...toAdd]
}

/**
 * True when the owner row for `uid` should render its checked/highlighted style.
 * While the dropdown is open, `pending` (the local in-dropdown selection) is the
 * source of truth; when there is no pending selection (dropdown closed), fall
 * back to the persisted committed owner ids so the button label stays accurate.
 */
export function isOwnerSelected(
  pending: Array<string | number> | null | undefined,
  committedOwnerIds: Array<string | number>,
  uid: string | number,
): boolean {
  const base = Array.isArray(pending)
    ? pending
    : (Array.isArray(committedOwnerIds) ? committedOwnerIds : [])
  return base.map(String).includes(String(uid))
}

/** True when two id sets are identical (order-independent, string-compared). */
export function sameOwnerIdSet(
  a: Array<string | number>,
  b: Array<string | number>,
): boolean {
  const sa = (Array.isArray(a) ? a : []).map(String)
  const sb = (Array.isArray(b) ? b : []).map(String)
  if (sa.length !== sb.length) return false
  const setB = new Set(sb)
  return sa.every((id) => setB.has(id))
}
